import { WidgetSDK, type IWidgetSDK } from '../core'
import { injectDesignTokens } from './design-tokens'
import type { WidgetInitFunction } from './widget.types'

const sdkInstances = new Map<ShadowRoot, IWidgetSDK>()
let currentInitializingSDK: IWidgetSDK | null = null

/**
 * `<gs-cc-registry-widget>` — Bridge for integrating external/third-party
 * widgets inside the community platform.
 *
 * Creates an SDK instance, executes widget scripts, and provides the
 * `init(sdk)` contract for registry widget authors. Content comes from the
 * widget registry via PHP `wrapInWebComponent()`.
 */
export class RegistryWidgetElement extends HTMLElement {
  private sdk: WidgetSDK | null = null

  static get observedAttributes(): string[] {
    return ['data-config']
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'data-config' && this.sdk && oldValue !== newValue) {
      try {
        const newProps = newValue ? JSON.parse(newValue) : {}
        this.sdk.setProps(newProps)
      } catch (error) {
        console.error('[WidgetSDK] Failed to parse data-config:', error)
      }
    }
  }

  connectedCallback(): void {
    if (this.shadowRoot) {
      this.initializeSDK(this.shadowRoot)
      return
    }

    const template = this.querySelector<HTMLTemplateElement>('template[shadowrootmode]')

    if (!template) {
      console.warn('[WidgetSDK] RegistryWidget: No shadow root or template found.')
      return
    }

    const mode = (template.getAttribute('shadowrootmode') ?? 'open') as ShadowRootMode
    const shadowRoot = this.attachShadow({ mode })
    shadowRoot.appendChild(template.content.cloneNode(true))
    template.remove()

    this.initializeSDK(shadowRoot)
  }

  disconnectedCallback(): void {
    if (this.sdk) {
      this.sdk._destroy()
      if (this.shadowRoot) {
        sdkInstances.delete(this.shadowRoot)
      }
      this.sdk = null
    }
  }

  private initializeSDK(shadowRoot: ShadowRoot): void {
    const widgetType = this.dataset.widgetType ?? 'unknown'
    const configAttr = this.dataset.config

    let props: Record<string, unknown> = {}
    if (configAttr) {
      try {
        props = JSON.parse(configAttr)
      } catch (error) {
        console.error('[WidgetSDK] Failed to parse data-config:', error)
      }
    }

    injectDesignTokens(shadowRoot)

    this.sdk = new WidgetSDK({ widgetType, props, shadowRoot, instanceId: this.dataset.instanceId })
    sdkInstances.set(shadowRoot, this.sdk)

    // Dev widget without attributes: resolve source/content from dev server
    if (widgetType.startsWith('registry:dev:') && !this.dataset.sourcePath && !this.dataset.contentEndpoint) {
      this.resolveDevWidget(shadowRoot, widgetType)
      return
    }

    // Dev reload: re-fetch content and re-render on __dev:reload event
    if (this.dataset.contentEndpoint) {
      this.sdk.on('__dev:reload', () => {
        this.reloadContent(shadowRoot)
      })
    }

    this.startRendering(shadowRoot)
  }

  private startRendering(shadowRoot: ShadowRoot): void {
    const contentEndpoint = this.dataset.contentEndpoint
    // Check for real content — ignore design token style and whitespace text nodes
    const hasContent =
      shadowRoot.querySelector('script') !== null ||
      shadowRoot.querySelector('div, p, span, section, article, main, header, footer') !== null

    if (contentEndpoint && !hasContent) {
      this.fetchAndRenderContent(shadowRoot, contentEndpoint)
    } else {
      this.executeScriptsAndLoadSource(shadowRoot)
    }
  }

  private async resolveDevWidget(shadowRoot: ShadowRoot, widgetType: string): Promise<void> {
    try {
      const stored = localStorage.getItem('devWidget.config')
      if (!stored) {
        this.sdk?._markReady()
        return
      }

      const { port } = JSON.parse(stored)
      const res = await fetch(`http://localhost:${port}/metadata`)
      if (!res.ok) {
        this.sdk?._markReady()
        return
      }

      const body = await res.json()
      const widgetMeta = body.data?.[widgetType]
      if (!widgetMeta) {
        this.sdk?._markReady()
        return
      }

      if (widgetMeta.source?.path) {
        this.dataset.sourcePath = widgetMeta.source.path
        this.dataset.sourceEntry = widgetMeta.source.entry ?? 'init'
      }
      if (widgetMeta.content?.endpoint) {
        this.dataset.contentEndpoint = widgetMeta.content.endpoint
        this.sdk?.on('__dev:reload', () => {
          this.reloadContent(shadowRoot)
        })
      }

      this.startRendering(shadowRoot)
    } catch {
      this.sdk?._markReady()
    }
  }

  private reloadContent(shadowRoot: ShadowRoot): void {
    const endpoint = this.dataset.contentEndpoint
    if (!endpoint) return

    // Destroy current SDK — triggers widget cleanup (intervals, listeners, subscriptions)
    if (this.sdk) {
      this.sdk._destroy()
      sdkInstances.delete(shadowRoot)
    }

    // Clear shadow root content but preserve design tokens
    const children = Array.from(shadowRoot.childNodes)
    for (const child of children) {
      if (child instanceof HTMLElement && child.id === 'css-variables') continue
      child.remove()
    }

    // Create fresh SDK instance
    const widgetType = this.dataset.widgetType ?? 'unknown'
    let props: Record<string, unknown> = {}
    try {
      props = this.dataset.config ? JSON.parse(this.dataset.config) : {}
    } catch {
      // keep empty props
    }

    this.sdk = new WidgetSDK({ widgetType, props, shadowRoot, instanceId: this.dataset.instanceId })
    sdkInstances.set(shadowRoot, this.sdk)

    // Re-register reload listener on the new SDK
    this.sdk.on('__dev:reload', () => {
      this.reloadContent(shadowRoot)
    })

    this.fetchAndRenderContent(shadowRoot, endpoint)
  }

  private async fetchAndRenderContent(shadowRoot: ShadowRoot, endpoint: string): Promise<void> {
    try {
      const response = await fetch(endpoint)
      if (!response.ok) {
        console.error(`[WidgetSDK] Content fetch failed: ${response.status} ${response.statusText}`)
        this.sdk?._markReady()
        return
      }

      const html = await response.text()
      const cleaned = RegistryWidgetElement.extractContent(html)

      // Inject fetched content into shadow root (after design tokens)
      const container = document.createElement('div')
      container.innerHTML = cleaned
      while (container.firstChild) {
        shadowRoot.appendChild(container.firstChild)
      }

      this.executeScriptsAndLoadSource(shadowRoot)
    } catch (error) {
      console.error('[WidgetSDK] Failed to fetch content:', endpoint, error)
      this.sdk?.emit('error', {
        message: `Failed to fetch widget content: ${endpoint}`,
        error: error instanceof Error ? error : new Error(String(error))
      })
      this.sdk?._markReady()
    }
  }

  private executeScriptsAndLoadSource(shadowRoot: ShadowRoot): void {
    this.executeScripts(shadowRoot)

    // CSR path: load module from source attributes when no scripts were in template
    // or after content fetch completes (hybrid mode)
    const sourcePath = this.dataset.sourcePath
    if (sourcePath) {
      const hasScriptsFromContent = shadowRoot.querySelector('script') !== null
      // In hybrid mode (content + source), always load the source module.
      // In source-only mode, load when no scripts exist.
      if (this.dataset.contentEndpoint || !hasScriptsFromContent) {
        const entry = this.dataset.sourceEntry ?? 'init'
        this.loadSourceModule(sourcePath, entry)
      }
    }

    this.sdk?._markReady()
  }

  /**
   * Strips <html>, <head>, <body> wrappers from fetched HTML content.
   * Returns only the inner content suitable for shadow DOM injection.
   */
  private static extractContent(html: string): string {
    // If it looks like a full HTML document, extract the body content
    if (html.includes('<body') || html.includes('<html')) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      return doc.body.innerHTML
    }
    // Otherwise it's already a partial — use as-is
    return html
  }

  private executeScripts(root: ShadowRoot): void {
    const scripts = root.querySelectorAll('script')

    scripts.forEach((origScript) => {
      currentInitializingSDK = this.sdk

      try {
        const newScript = document.createElement('script')

        for (const attr of origScript.attributes) {
          newScript.setAttribute(attr.name, attr.value)
        }

        if (origScript.src) {
          newScript.src = origScript.src
          if (origScript.type === 'module') {
            this.handleModuleScript(origScript.src)
          }
        } else if (origScript.type === 'module' && origScript.textContent) {
          // Inline module script - convert to blob URL for dynamic import
          const blob = new Blob([origScript.textContent], { type: 'text/javascript' })
          const blobUrl = URL.createObjectURL(blob)
          this.handleModuleScript(blobUrl).finally(() => URL.revokeObjectURL(blobUrl))
        } else {
          newScript.textContent = origScript.textContent
        }

        origScript.parentNode?.replaceChild(newScript, origScript)
      } finally {
        currentInitializingSDK = null
      }
    })
  }

  private async loadSourceModule(src: string, entry: string): Promise<void> {
    if (!this.sdk) return

    try {
      const module = await import(/* @vite-ignore */ src)
      const fn = module[entry] ?? module.default

      if (typeof fn === 'function') {
        await (fn as WidgetInitFunction)(this.sdk)
      } else {
        console.warn(`[WidgetSDK] Module at ${src} has no "${entry}" or default export`)
      }
    } catch (error) {
      console.error('[WidgetSDK] Failed to load source module:', src, error)
      this.sdk?.emit('error', {
        message: `Failed to load widget module: ${src}`,
        error: error instanceof Error ? error : new Error(String(error))
      })
    }
  }

  private async handleModuleScript(src: string): Promise<void> {
    if (!this.sdk) return

    try {
      const module = await import(/* @vite-ignore */ src)

      if (typeof module.init === 'function') {
        await (module.init as WidgetInitFunction)(this.sdk)
      } else if (typeof module.default === 'function') {
        await (module.default as WidgetInitFunction)(this.sdk)
      }
    } catch (error) {
      console.error('[WidgetSDK] Failed to load/initialize module:', src, error)
      this.sdk.emit('error', {
        message: `Failed to load widget module: ${src}`,
        error: error instanceof Error ? error : new Error(String(error))
      })
    }
  }
}

export function getCurrentWidgetSDK(): IWidgetSDK | null {
  if (currentInitializingSDK) {
    return currentInitializingSDK
  }

  const currentScript = document.currentScript
  if (currentScript) {
    const root = currentScript.getRootNode()
    if (root instanceof ShadowRoot) {
      return sdkInstances.get(root) ?? null
    }
  }

  return null
}

export function getSDKForShadowRoot(shadowRoot: ShadowRoot): IWidgetSDK | null {
  return sdkInstances.get(shadowRoot) ?? null
}

if (typeof customElements !== 'undefined' && !customElements.get('gs-cc-registry-widget')) {
  customElements.define('gs-cc-registry-widget', RegistryWidgetElement)
}

if (globalThis.window !== undefined) {
  globalThis.window.getCurrentWidgetSDK = getCurrentWidgetSDK
  globalThis.window.__WIDGET_SDK_INSTANCES__ = sdkInstances
}
